import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Divider,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { differenceInDays } from "date-fns";
import React, { useEffect, useRef, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { FaQuestionCircle } from "react-icons/fa";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";

import { useProject } from "../../../contexts/project.context";
import ProjectService from "../../../services/project.service";
import { BudgetService } from "../../../services/budget.service";
import { CepData, TabActive } from "../../../types";
import { isAbbottClient as checkIsAbbottClient } from "../../../constants/abbottClient";
import { isEliLillyClient as checkIsEliLillyClient } from "../../../constants/eliLillyClient";
import {
  DirectPaymentEnum,
  EstimateTypeEnum,
  EventScopeEnum,
  InvoiceTypeEnum,
  PaymentMethodEnum,
} from "../../../types/project/enums";
import { createOptionsFromEnum } from "../../../utils/formatters";
import {
  normalizeProjectType,
  projectTypeDescription,
} from "../../../utils/opcoesBriefingInicial";
import { getEventFieldOptions } from "../../../utils/eventFieldOptions";
import CurrencyInputForm from "../../CurrencyInputForm";
import DatePickerForm from "../../DatePickerForm";
import EventPeriodsInput, {
  EventPeriod,
  parsePeriodsFromBackend,
} from "../../EventPeriodsInput";
import { Input } from "../../Input";
import Select from "../../UI/Select";
import TriStateSwitch from "../../UI/TriStateSwitch";
import YesOrNoSwitch from "../../UI/YesOrNoSwitch";
import { useQuery, useQueryClient } from "react-query";

import TabContent from "../components/TabContent";
import RolePermissionsService from "../../../services/rolesPermissions.service";
import { useProfile } from "../../../queries/hooks";
import ColorToggleButton from "../../ToggleButtonGroup";
import ExchangeFields from "../../UI/ExchangeFields";
import {
  getCoinOptions,
  getEventExchange,
  applyEventExchangeToBaseValues,
} from "../../../utils/exchangeManager";
import { useBudget } from "../../../contexts/BudgetContext/projectbudgets.context";
import { isFullBudget } from "../../../utils/budget-adapters";
import { Budget } from "../../../types/budget/budget";
import {
  CustomFieldOptionField,
  ManagementFieldRequiredIn,
  ManagementFieldType,
} from "../../../types/client.type";
import {
  getRequiredIn,
  managementFieldValuesSchema,
  sortManagementFields,
} from "../../../utils/managementFields";
import { useServices } from "../../../contexts/ServicesContext/projectservices.context";

const managementFieldElementId = (name: string) =>
  `management-field-${encodeURIComponent(name)}`;

const estimateOptions = createOptionsFromEnum(EstimateTypeEnum);
const billingOptions = createOptionsFromEnum(InvoiceTypeEnum);
const paymentOptions = createOptionsFromEnum(PaymentMethodEnum);

const eventScopeOptions = [
  { value: EventScopeEnum.NATIONAL, label: "Nacional" },
  { value: EventScopeEnum.INTERNATIONAL, label: "Internacional" },
  { value: EventScopeEnum.BOTH, label: "Ambos" },
];

const coinOptions = getCoinOptions();

const directPaymentOptions = [
  { value: DirectPaymentEnum.YES, label: "Sim", activeColor: "#1976D2" },
  { value: DirectPaymentEnum.NO, label: "Não", activeColor: "#EA5455" },
  { value: DirectPaymentEnum.BOTH, label: "Ambos", activeColor: "#1976D2" },
];

interface ProjectEventProps {
  onBack?: () => void;
}

const ProjectEvent = ({ onBack }: ProjectEventProps) => {
  const {
    event,
    updateEvent,
    project,
    clearProject,
    client,
    tabActive,
    loadProject,
    pendingManagementFields,
    clearPendingManagementFields,
  } = useProject();
  // Campos obrigatórios por status que bloquearam a troca de status: ficam
  // destacados enquanto estiverem vazios.
  const [highlightedManagementFields, setHighlightedManagementFields] =
    useState<string[]>([]);
  const history = useHistory();
  const queryClient = useQueryClient();
  const {
    activeBudget,
    activeBudgetDispatch,
    updateBudget,
    budgetList,
    versionInfo,
    isBudgetDisabled,
    loadBudgetList,
    setDetailedBudget,
  } = useBudget();
  const { activeService, activeServiceDispatch } = useServices();

  const [nameExistsError, setNameExistsError] = useState(false);
  const nameDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevExchangeRef = useRef<{
    date?: string;
    currency?: string;
    value?: number;
  }>({});
  const prevClientRef = useRef<string | undefined>(undefined);
  const [shouldUpdateActiveBudget, setShouldUpdateActiveBudget] =
    useState(false);
  const [hostingPeriods, setHostingPeriods] = useState<EventPeriod[]>([]);

  // Atualiza activeBudget quando budgetList muda (após loadBudgetList)
  useEffect(() => {
    if (shouldUpdateActiveBudget && activeBudget?._id && budgetList) {
      const updatedBudget = budgetList.find((b) => b._id === activeBudget._id);
      // Skip summary-only rows — activeBudget must remain a full document.
      if (updatedBudget && isFullBudget(updatedBudget)) {
        // Primeiro remove o activeBudget
        activeBudgetDispatch({
          type: "NO_ACTIVE_BUDGET",
          payload: undefined,
        });

        // Depois adiciona novamente com os dados atualizados
        // Deep clone para quebrar todas as referências e forçar re-render
        setTimeout(() => {
          const deepClonedBudget = JSON.parse(JSON.stringify(updatedBudget));
          activeBudgetDispatch({
            type: "NEW_ACTIVE_BUDGET",
            payload: { ...deepClonedBudget, isApiUpdate: true },
          });

          // Força re-render do ServiceItem mudando detailedBudget
          const currentDetailedBudget = `${deepClonedBudget._id}-version-${
            versionInfo?.version || 0
          }`;
          setDetailedBudget("");
          setTimeout(() => {
            setDetailedBudget(currentDetailedBudget);
          }, 10);
        }, 50);
      }
      setShouldUpdateActiveBudget(false);
    }
  }, [budgetList, shouldUpdateActiveBudget, activeBudget?._id]);

  const { data: userData } = useProfile();

  const { data: rolesData } = useQuery({
    queryKey: ["roles"],
    queryFn: () => RolePermissionsService.allRoles(),
  });

  const managers = rolesData?.find(
    (role: any) => role?.name?.toLowerCase() === "gerente"
  );

  const isUserManager = managers?.users?.some(
    (user: any) => user._id === userData?._id
  );

  function isRequiredDueToAprovedSTatus(message: string) {
    return project?.status.status === "APPROVED"
      ? yup.string().required(message)
      : yup.string().nullable();
  }

  function isPfizerClient(): boolean {
    const clientName = client?.clientSelected?.name || "";
    return clientName.toUpperCase().includes("PFIZER");
  }

  function isAbbottClient(): boolean {
    return checkIsAbbottClient(client?.clientSelected?.name);
  }

  function isEliLillyClient(): boolean {
    return checkIsEliLillyClient(client?.clientSelected?.name);
  }

  const schema = yup
    .object({
      name: yup
        .string()
        .max(99, "Nome do evento deve ter no máximo 99 caracteres")
        .required("Nome do evento é obrigatório"),
      type: yup
        .string()
        .required("Tipo de evento é obrigatório")
        .test("not-empty", "Tipo de evento é obrigatório", (value) => !!value),
      local: isRequiredDueToAprovedSTatus("Local é obrigatório"),
      addressFixed: yup.boolean().required("Endereço fixo é obrigatório"),
      eventFormat: yup.string().required("Formato de evento é obrigatório"),
      metrics: yup.string(),
      customerBudgetObservations: yup.string(),
      zip_code: isRequiredDueToAprovedSTatus("CEP é obrigatório"),
      city: isRequiredDueToAprovedSTatus("Cidade é obrigatório"),
      number: yup.string().nullable(),
      state: isRequiredDueToAprovedSTatus("Estado é obrigatório"),
      country: isRequiredDueToAprovedSTatus("Páis é obrigatório"),
      complement: isRequiredDueToAprovedSTatus("Logradouro é obrigatório"),
      complement2: isRequiredDueToAprovedSTatus("Complemento é obrigatório"),
      address: yup.string(),
      neighborhood: isRequiredDueToAprovedSTatus("Bairro é obrigatório"),
      street_type: yup.string(),
      eventProfile: yup.string(),
      audience: yup.string(),
      numberOfPeople: yup
        .number()
        .typeError("Informe um número válido de pessoas")
        .positive("Informe um número válido de pessoas")
        .integer("Informe um número válido de pessoas")
        .min(1, "Informe um número válido de pessoas")
        .required("Número de pessoas é obrigatório"),
      startDate: yup
        .date()
        .typeError("Data inválida")
        .required("Data início é obrigatório")
        .test(
          "is-after-2015",
          "Data não pode ser anterior a 2015",
          (value: Date | undefined): boolean => {
            if (!value) return true;
            const selectedDate = new Date(value);
            const minDate = new Date(2015, 0, 1);
            return selectedDate >= minDate;
          }
        )
        .test(
          "not-retroactive",
          "Data início não pode ser retroativa",
          (value: Date | undefined): boolean => {
            // Gerentes podem cadastrar datas retroativas (mesma exceção do minDate)
            if (isUserManager) return true;
            if (!value) return true;
            const selected = new Date(value);
            selected.setHours(0, 0, 0, 0);

            // Em edição de projeto já existente, a data de início não deve ser
            // revalidada como retroativa se o usuário não a alterou. Isso evita
            // bloquear a atualização de outros campos quando o evento já iniciou
            // ou está no passado. A validação segue valendo se a data for mudada.
            if (event?._id && event?.startDate) {
              const original = new Date(event.startDate);
              original.setHours(0, 0, 0, 0);
              if (selected.getTime() === original.getTime()) return true;
            }

            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return selected >= today;
          }
        ),
      endDate: yup
        .date()
        .typeError("Data inválida")
        .required("Data fim é obrigatória")
        .test(
          "is-after-2015",
          "Data não pode ser anterior a 2015",
          (value: Date | undefined): boolean => {
            if (!value) return true;
            const selectedDate = new Date(value);
            const minDate = new Date(2015, 0, 1);
            return selectedDate >= minDate;
          }
        )
        .min(
          yup.ref("startDate"),
          "Data fim não pode ser anterior à data inicial"
        ),
      sequenceDays: yup.boolean().required("Dias corridos é obrigatório"),
      duration: yup.number(),
      sameTimeEveryDay: yup
        .boolean()
        .required("Mesmo horário início e fim todos os dias é obrigatório"),
      objective: yup.string(),
      observationsEvent: yup.string(),
      customerChallenge: yup.string(),
      eventScope: yup.string().required("Evento é obrigatório"),
      hasCompetitor: yup.boolean().required("Concorrência é obrigatório"),
      numberOfCompetitors: yup.number().nullable(),
      briefingStartDate: yup
        .date()
        .typeError("Data inválida")
        .required("Inicio Briefing é obrigatório")
        .test(
          "is-after-2015",
          "Data não pode ser anterior a 2015",
          function (value: Date | undefined): boolean {
            if (!value) return true;
            const selectedDate = new Date(value);
            const minDate = new Date(2015, 0, 1);
            return selectedDate >= minDate;
          }
        ),
      sendDate: yup
        .date()
        .typeError("Data inválida")
        .required("Envio de Briefing é obrigatório")
        .test(
          "is-after-2015",
          "Data não pode ser anterior a 2015",
          function (value: Date | undefined): boolean {
            if (!value) return true;
            const selectedDate = new Date(value);
            const minDate = new Date(2015, 0, 1);
            return selectedDate >= minDate;
          }
        ),
      presentationDate: yup
        .date()
        .typeError("Data inválida")
        // .nullable()
        .required("Data de Apresentação é Obrigatória")
        .test(
          "is-after-2015",
          "Data não pode ser anterior a 2015",
          function (value: Date | undefined): boolean {
            if (!value) return true;
            const selectedDate = new Date(value);
            const minDate = new Date(2015, 0, 1);
            return selectedDate >= minDate;
          }
        ),
      observationsBriefing: yup.string(),
      pfzCode: yup.string(),
      mappCode: yup.string(),
      cventCode: yup.string(),
      // ELI LILLY fields
      eliLillyInternalOrder: yup.string(),
      eliLillyCostCenter: yup.string(),
      eliLillyPurchaseOrder: yup.string(),
      eliLillyVemId: yup.string(),
      eliLillyProduct: yup.string(),
      eliLillyBuDepartment: yup.string(),
      managementFieldValues: managementFieldValuesSchema(
        client?.clientSelected?.management_fields
      ),
      customerBudget: yup
        .number()
        .typeError("Valor de Budget do cliente é obrigatório")
        .min(1, "Valor de Budget do cliente é obrigatório")
        .required("Valor de Budget do cliente é obrigatório"),
      paymentTerm: yup.string(),
      acceptAdvances: yup.boolean().required("Faz adiantamento é obrigatório"),
      advanceObservations: yup.string(),
      hasDrawnRisk: yup.boolean().required("Possui risco sacado é obrigatório"),
      monthlyTax: yup
        .number()
        .nullable()
        .transform((value, originalValue) => {
          // Transform empty string to null
          return originalValue === "" ? null : value;
        })
        .when("hasDrawnRisk", {
          is: true,
          then: (schema) =>
            schema.required(
              "Taxa mensal é obrigatória quando possui risco sacado"
            ),
          otherwise: (schema) => schema.nullable(),
        }),
      drawnRiskObservations: yup.string(),
      directPayment: yup
        .string()
        .required("Paga fornecedores diretamente é obrigatório"),
      directPaymentsObservations: yup.string(),
      fee: yup.string(),
      paymentMethod: yup.string(),
      paymentMethodObservations: yup.string(),
      billingMethod: yup.string(),
      hourInit: yup.string(),
      hourEnd: yup.string(),
      schedulleObservations: yup.string(),
      customerBudgetType: yup
        .string()
        .required("Preencha o campo Tipo de estimativa"),
      exchangeDate: yup.string().when("eventScope", {
        is: (val: string) =>
          val === EventScopeEnum.INTERNATIONAL || val === EventScopeEnum.BOTH,
        then: (schema) =>
          schema.required(
            "Data da cotação é obrigatória para eventos internacionais"
          ),
        otherwise: (schema) => schema.nullable(),
      }),
      exchangeCurrency: yup.string().when("eventScope", {
        is: (val: string) =>
          val === EventScopeEnum.INTERNATIONAL || val === EventScopeEnum.BOTH,
        then: (schema) =>
          schema.required("Moeda é obrigatória para eventos internacionais"),
        otherwise: (schema) => schema.nullable(),
      }),
      exchangeValue: yup.number().when("eventScope", {
        is: (val: string) =>
          val === EventScopeEnum.INTERNATIONAL || val === EventScopeEnum.BOTH,
        then: (schema) =>
          schema
            .required(
              "Valor do câmbio é obrigatório para eventos internacionais"
            )
            .min(0.01, "Valor deve ser maior que zero"),
        otherwise: (schema) => schema.nullable(),
      }),
    })
    .required();

  const methods = useForm({
    defaultValues: {
      name: "",
      type: "",
      local: "",
      addressFixed: undefined,
      eventFormat: "presential",
      metrics: "",
      zip_code: "",
      number: "",
      country: "",
      state: "",
      city: "",
      complement: "",
      neighborhood: "",
      eventProfile: "",
      audience: "",
      numberOfPeople: undefined,
      startDate: undefined,
      endDate: undefined,
      sequenceDays: undefined,
      duration: 0,
      sameTimeEveryDay: undefined,
      objective: "",
      observationsEvent: "",
      customerChallenge: "",
      eventScope: undefined,
      hasCompetitor: undefined,
      numberOfCompetitors: 0,
      briefingStartDate: undefined,
      sendDate: undefined,
      presentationDate: undefined,
      observationsBriefing: "",
      pfzCode: "",
      mappCode: "",
      cventCode: "",
      // ELI LILLY fields
      eliLillyInternalOrder: "",
      eliLillyCostCenter: "",
      eliLillyPurchaseOrder: "",
      eliLillyVemId: "",
      eliLillyProduct: "",
      eliLillyBuDepartment: "",
      managementFieldValues: {},
      customerBudget: 0,
      paymentTerm: "",
      acceptAdvances: undefined,
      advanceObservations: "",
      hasDrawnRisk: undefined,
      monthlyTax: undefined,
      drawnRiskObservations: "",
      directPayment: undefined,
      directPaymentsObservations: "",
      fee: "",
      paymentMethod: "none",
      billingMethod: "",
      paymentMethodObservations: "",
      hourInit: "",
      hourEnd: "",
      schedulleObservations: "",
      customerBudgetObservations: "",
      customerBudgetType: undefined,
      exchangeDate: "",
      exchangeCurrency: "BRL",
      exchangeValue: 1,
    },
    resolver: yupResolver(schema),
    mode: "all",
  });
  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    getFieldState,
    trigger,
    control,
    formState: { errors },
  } = methods;
  const managementFieldValuesWatch = watch("managementFieldValues") as
    | Record<string, string>
    | undefined;

  const [cepData, setCepData] = useState<CepData | null>(null);

  const fetchCepData = async (cep: any) => {
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      setCepData(data);
      setValue("country", data?.erro ? "" : "Brasil");
    } catch (error) {
      console.error("Error fetching CEP data:", error);
    }
  };

  useEffect(() => {
    if (cepData) {
      setValue("complement", cepData.logradouro || "");
      setValue("neighborhood", cepData.bairro || "");
      setValue("city", cepData.localidade || "");
      setValue("state", cepData.uf || "");
    }
  }, [cepData, setValue]);

  const eventScopeWatch = watch("eventScope");
  const hasCompetitorWatch = watch("hasCompetitor");
  const addressFixedWatch = watch("addressFixed");
  const sequenceDaysWatch = watch("sequenceDays");
  const sameTimeEveryDayWatch = watch("sameTimeEveryDay");
  const startDateWatch = watch("startDate");
  const endDateWatch = watch("endDate");
  const typeWatch = watch("type");
  const eventFormatWatch = watch("eventFormat");
  // Opções padrão + personalizadas do cliente, em ordem alfabética; o valor já
  // gravado continua disponível mesmo que a opção tenha sido removida.
  const eventTypes = getEventFieldOptions(
    CustomFieldOptionField.EVENT_TYPE,
    client?.clientSelected?.custom_field_options,
    normalizeProjectType(typeWatch)
  );
  const eventFormats = getEventFieldOptions(
    CustomFieldOptionField.EVENT_FORMAT,
    client?.clientSelected?.custom_field_options,
    eventFormatWatch
  );
  const acceptAdvancesWatch = watch("acceptAdvances");
  const hasDrawnRiskWatch = watch("hasDrawnRisk");
  const directPaymentWatch = watch("directPayment");
  const exchangeDateWatch = watch("exchangeDate");
  const exchangeCurrencyWatch = watch("exchangeCurrency");
  const exchangeValueWatch = watch("exchangeValue");

  function updateExchangeValues(type: string, value: number, calcType: string) {
    setValue("exchangeCurrency", calcType);
    setValue("exchangeValue", value);
  }

  useEffect(() => {
    if (startDateWatch && endDateWatch) {
      const daysDifference = differenceInDays(endDateWatch, startDateWatch);
      // if(daysDifference < 0) {
      //    toast.error('Data fim não pode ser anterior à data inicio')
      //    setValue('endDate', startDateWatch)
      //    return
      // }
      const duration = daysDifference + 1;
      setValue("duration", duration);
    }
  }, [startDateWatch, endDateWatch, setValue]);

  async function onSubmit(form: any) {
    if (nameExistsError) {
      toast.error("Já existe um projeto com esse nome de evento.");
      return;
    }

    // Atualiza câmbio nas categorias ANTES de salvar (data é opcional)
    if (form.exchangeCurrency && form.exchangeValue) {
      const newExchange = getEventExchange({
        exchangeDate: form.exchangeDate || "",
        exchangeCurrency: form.exchangeCurrency,
        exchangeValue: form.exchangeValue,
      });

      if (newExchange) {
        // Se está em Dados Cadastrais (INITIAL), usa o backend para atualizar todos os orçamentos
        if (tabActive === TabActive.INITIAL && project?._id) {
          try {
            // Chama o backend para atualizar câmbio de todos os orçamentos do projeto
            await BudgetService.updateProjectExchange(project._id, newExchange);

            // Pequeno delay para garantir que o banco foi atualizado
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Invalida cache do React Query
            await queryClient.invalidateQueries();

            // Recarrega projeto e orçamentos
            await loadProject(project._id);
            await loadBudgetList(project._id);

            // Ativa flag para atualizar activeBudget no useEffect
            setShouldUpdateActiveBudget(true);

            toast.success("Câmbio atualizado! Recarregando dados...");
          } catch (error) {
            toast.error("Erro ao atualizar câmbio");
          }
        }
        // Se está em abas de orçamento (BUDGET, NEGOTIATION, APPROVED) E orçamento está habilitado
        else if (activeBudget?._id && !isBudgetDisabled) {
          // CÓDIGO ANTIGO - mantém para quando estiver em outras abas
          for (const row of budgetList || []) {
            if (!row._id || !row.versionHistory) continue;
            // Prefer hydrated activeBudget; skip summary-only list rows.
            const budget: Budget | undefined =
              row._id === activeBudget?._id
                ? activeBudget
                : isFullBudget(row)
                ? row
                : undefined;
            if (!budget?._id) continue;

            // Função que verifica se uma versão é editável (mesma lógica do isBudgetDisabled)
            const isVersionEditable = (v: any) => {
              // Assessoria é sempre editável
              if (v.isConsulting) return true;

              // Para orçamento original (v0)
              if (v.version === 0 && v.status === "OPEN") {
                // Editável se não houver negociações ou fechamentos
                return !budget.versionHistory.some(
                  (vh) => vh.status === "NEGOTIATE" || vh.status === "APPROVED"
                );
              }

              // Para negociações
              if (v.status === "NEGOTIATE") {
                const lastNeg = budget.versionHistory
                  .filter((vh) => vh.status === "NEGOTIATE" && !vh.isConsulting)
                  .reduce(
                    (max, vh) => (vh.version > max ? vh.version : max),
                    0
                  );
                const hasApproved = budget.versionHistory.some(
                  (vh) => vh.status === "APPROVED"
                );
                // Editável se for a última negociação E não houver fechamento
                return v.version === lastNeg && !hasApproved;
              }

              // Para fechamentos - sempre atualiza câmbio
              if (v.status === "APPROVED") {
                const lastApproved = budget.versionHistory
                  .filter((vh) => vh.status === "APPROVED")
                  .reduce(
                    (max, vh) => (vh.version > max ? vh.version : max),
                    0
                  );
                return v.version === lastApproved;
              }

              return false;
            };

            // Filtra apenas versões editáveis (editável ou assessoria)
            const versionsToUpdate =
              budget.versionHistory.filter(isVersionEditable);

            // Se não há versões editáveis, pula este orçamento
            if (versionsToUpdate.length === 0) continue;

            // IMPORTANTE: baseValues não é separado por versão!
            // Apenas atualiza baseValues se for v0 (orçamento original sem negociação)
            const shouldUpdateBaseValues = versionsToUpdate.some(
              (v) => v.version === 0 && v.status === "OPEN"
            );
            // Categorias com câmbio personalizado são preservadas: só saem dele
            // pelo "Restaurar padrão" da própria categoria.
            const updatedBaseValues = shouldUpdateBaseValues
              ? applyEventExchangeToBaseValues(budget.baseValues, newExchange)
              : budget.baseValues || [];

            // Atualiza serviços de TODAS as versões habilitadas
            // IMPORTANTE: Fazer deep clone para evitar mutação do original
            const updatedServices =
              budget.budgetServices?.map((service) => {
                // Deep clone do serviço para evitar referências compartilhadas
                const updatedService = JSON.parse(JSON.stringify(service));

                // Processa cada versão que deve ser atualizada
                for (const versionToUpdate of versionsToUpdate) {
                  if (versionToUpdate.status === "APPROVED") {
                    // Atualiza closingValues (Fechamento)
                    updatedService.closingValues = {
                      ...updatedService.closingValues,
                      exchangeData: newExchange,
                      exchangeOverride: false,
                    };
                  } else if (
                    versionToUpdate.version > 0 &&
                    versionToUpdate.status === "NEGOTIATE"
                  ) {
                    // Atualiza negotiationValue (Negociação)
                    // Conta quantas negociações existem ANTES desta versão
                    const negotiationsBeforeThis = budget.versionHistory.filter(
                      (vh) =>
                        vh.status === "NEGOTIATE" &&
                        vh.version < versionToUpdate.version
                    ).length;

                    const negotiationIndex = negotiationsBeforeThis;

                    // Só atualiza se negotiationValue[index] já existir
                    if (
                      updatedService.negotiationValue &&
                      updatedService.negotiationValue[negotiationIndex]
                    ) {
                      updatedService.negotiationValue[negotiationIndex] = {
                        ...updatedService.negotiationValue[negotiationIndex],
                        exchangeData: newExchange,
                        exchangeOverride: false,
                      };
                    }
                  } else if (versionToUpdate.version === 0) {
                    // Atualiza budgetValues (v0 - Orçamento)
                    updatedService.budgetValues = {
                      ...updatedService.budgetValues,
                      exchangeData: newExchange,
                      exchangeOverride: false,
                    };
                  }
                }

                return updatedService;
              }) || [];

            // Atualiza diretamente o budget (como a aba Negociação faz)
            budget.baseValues = updatedBaseValues;
            budget.budgetServices = updatedServices;

            // Se este budget é o activeBudget, atualiza ele também
            if (activeBudget?._id === budget._id) {
              activeBudget.baseValues = updatedBaseValues;
              activeBudget.budgetServices = updatedServices;

              // Força dispatch para re-render
              activeBudgetDispatch({
                type: "NEW_ACTIVE_BUDGET",
                payload: { ...activeBudget },
              });
            }

            // Salva o orçamento atualizado
            // Passa -1 como versão (como a aba Negociação faz)
            await updateBudget(budget._id, budget, "SAVE", -1);
          }
        }

        // Atualiza câmbio das categorias no Service
        if (activeService?._id) {
          activeServiceDispatch({
            type: "UPDATE_BASE_VALUES_EXCHANGE",
            payload: {
              eventExchange: newExchange,
              forceUpdate: true,
            },
          });

          // Service não precisa de save manual, autosave cuida disso
        }
      }
    }

    form.hostingPeriods = hostingPeriods
      .filter((p) => p.inicio && p.fim)
      .map((period) => ({
        inicio: period.inicio!.toISOString(),
        fim: period.fim!.toISOString(),
      }));
    await updateEvent(form);
    if (!project?._id) {
      await queryClient.resetQueries(["projects"]);
      clearProject();
      history.push("/projetos");
      toast.success("Projeto criado com sucesso");
    } else {
      toast.success("Projeto atualizado com sucesso");
    }
  }

  function handleBlur() {
    const dailyCalc = differenceInDays(endDateWatch, startDateWatch);
    if (dailyCalc < 0) {
      setValue("endDate", startDateWatch);
      return toast.error("Data fim não pode ser anterior à data inicio");
    }
  }

  useEffect(() => {
    if (event) {
      reset({
        ...event,
        type: event.type && event.type !== "none" ? event.type : "",
      });
      setHostingPeriods(parsePeriodsFromBackend(event.hostingPeriods || []));
    }
  }, [reset, event]);

  // Consome o pedido do contexto: sem isso, toda nova entrada em Dados
  // Cadastrais abriria direto em Dados do Evento.
  useEffect(() => {
    if (pendingManagementFields.length === 0) return;
    setHighlightedManagementFields(pendingManagementFields);
    clearPendingManagementFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingManagementFields]);

  useEffect(() => {
    if (highlightedManagementFields.length === 0) return;
    const timer = window.setTimeout(() => {
      document
        .getElementById(
          managementFieldElementId(highlightedManagementFields[0])
        )
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [highlightedManagementFields]);

  // Limpa campos PFZ e MAPP quando o cliente for alterado para não-Pfizer
  // Limpa campo CVENT quando o cliente for alterado para não-Abbott
  useEffect(() => {
    const currentClientId = client?.clientSelected?._id;
    const previousClientId = prevClientRef.current;

    // Verifica se houve mudança de cliente
    if (
      previousClientId &&
      currentClientId &&
      previousClientId !== currentClientId
    ) {
      // Se o novo cliente não é Pfizer, limpa os campos PFZ e MAPP
      if (!isPfizerClient()) {
        const currentPfzCode = getValues("pfzCode");
        const currentMappCode = getValues("mappCode");

        // Só limpa se os campos estiverem preenchidos
        if (currentPfzCode || currentMappCode) {
          setValue("pfzCode", "");
          setValue("mappCode", "");
        }
      }

      // Se o novo cliente não é Abbott, limpa o campo CVENT
      if (!isAbbottClient()) {
        const currentCventCode = getValues("cventCode");

        // Só limpa se o campo estiver preenchido
        if (currentCventCode) {
          setValue("cventCode", "");
        }
      }

      // Se o novo cliente não é ELI LILLY, limpa os campos ELI LILLY
      if (!isEliLillyClient()) {
        const currentInternalOrder = getValues("eliLillyInternalOrder");
        const currentCostCenter = getValues("eliLillyCostCenter");
        const currentPurchaseOrder = getValues("eliLillyPurchaseOrder");
        const currentVemId = getValues("eliLillyVemId");
        const currentProduct = getValues("eliLillyProduct");
        const currentBuDepartment = getValues("eliLillyBuDepartment");

        // Só limpa se pelo menos um campo estiver preenchido
        if (
          currentInternalOrder ||
          currentCostCenter ||
          currentPurchaseOrder ||
          currentVemId ||
          currentProduct ||
          currentBuDepartment
        ) {
          setValue("eliLillyInternalOrder", "");
          setValue("eliLillyCostCenter", "");
          setValue("eliLillyPurchaseOrder", "");
          setValue("eliLillyVemId", "");
          setValue("eliLillyProduct", "");
          setValue("eliLillyBuDepartment", "");
        }
      }

      setValue("managementFieldValues", {});
    }

    // Atualiza a referência do cliente anterior
    prevClientRef.current = currentClientId;
  }, [client?.clientSelected?._id, setValue, getValues]);

  // Define valores padrão de câmbio para Internacional/Ambos ou limpa para Nacional
  useEffect(() => {
    if (eventScopeWatch === EventScopeEnum.NATIONAL) {
      // Limpa campos de câmbio no formulário
      setValue("exchangeDate", "");
      setValue("exchangeCurrency", "");
      setValue("exchangeValue", undefined);

      // Atualiza câmbio das categorias para BRL padrão quando muda para Nacional
      const nationalExchange = getEventExchange({ eventScope: "Nacional" });

      if (nationalExchange) {
        // Atualiza câmbio das categorias no Budget APENAS se está habilitado
        if (activeBudget?._id && !isBudgetDisabled) {
          activeBudgetDispatch({
            type: "UPDATE_BASE_VALUES_EXCHANGE",
            payload: {
              eventExchange: nationalExchange,
              forceUpdate: true, // Força atualização mesmo com override
            },
            versionInfo: versionInfo,
          });
        }

        // Atualiza câmbio das categorias no Service
        if (activeService?._id) {
          activeServiceDispatch({
            type: "UPDATE_BASE_VALUES_EXCHANGE",
            payload: {
              eventExchange: nationalExchange,
              forceUpdate: true, // Força atualização mesmo com override
            },
          });
        }
      }
    } else if (
      eventScopeWatch === EventScopeEnum.INTERNATIONAL ||
      eventScopeWatch === EventScopeEnum.BOTH
    ) {
      // Só define padrões se for criação (não tem event._id) e não tem valores definidos
      const isCreating = !event?._id;

      // Data fica em branco para o usuário preencher
      if (isCreating && !exchangeCurrencyWatch) {
        setValue("exchangeCurrency", "BRL");
      }
      if (isCreating && !exchangeValueWatch) {
        setValue("exchangeValue", 1);
      }
    }
  }, [
    eventScopeWatch,
    exchangeDateWatch,
    exchangeCurrencyWatch,
    exchangeValueWatch,
    event,
    setValue,
    activeBudget,
    activeBudgetDispatch,
    activeService,
    activeServiceDispatch,
  ]);

  // Sincroniza câmbio com serviços e categorias quando houver mudança EM TEMPO REAL (durante digitação)
  useEffect(() => {
    const prev = prevExchangeRef.current;
    const hasChanged =
      prev.date !== exchangeDateWatch ||
      prev.currency !== exchangeCurrencyWatch ||
      prev.value !== exchangeValueWatch;

    // Permite atualização se moeda E valor estiverem preenchidos (data é opcional)
    if (hasChanged && exchangeCurrencyWatch && exchangeValueWatch) {
      const newExchange = getEventExchange({
        exchangeDate: exchangeDateWatch || "",
        exchangeCurrency: exchangeCurrencyWatch,
        exchangeValue: exchangeValueWatch,
      });

      if (newExchange) {
        // Atualiza câmbio APENAS se orçamento está habilitado para edição
        if (activeBudget?._id && !isBudgetDisabled) {
          activeBudgetDispatch({
            type: "UPDATE_SERVICES_EXCHANGE",
            payload: { eventExchange: newExchange },
            versionInfo: versionInfo,
          });

          // Atualiza câmbio das categorias (baseValues) no Budget
          // forceUpdate: false = respeita override manual durante digitação
          activeBudgetDispatch({
            type: "UPDATE_BASE_VALUES_EXCHANGE",
            payload: {
              eventExchange: newExchange,
              forceUpdate: false,
            },
            versionInfo: versionInfo,
          });
        }

        // Atualiza câmbio das categorias (baseValues) no Service
        if (activeService?._id) {
          activeServiceDispatch({
            type: "UPDATE_BASE_VALUES_EXCHANGE",
            payload: {
              eventExchange: newExchange,
              forceUpdate: false,
            },
          });
        }

        // Não exibe toast aqui, pois já será exibido no onSubmit
      }
    }

    prevExchangeRef.current = {
      date: exchangeDateWatch,
      currency: exchangeCurrencyWatch,
      value: exchangeValueWatch,
    };
  }, [
    exchangeDateWatch,
    exchangeCurrencyWatch,
    exchangeValueWatch,
    activeBudget,
    activeBudgetDispatch,
    activeService,
    activeServiceDispatch,
  ]);

  return (
    <FormProvider {...methods}>
      <TabContent
        onSubmit={handleSubmit(onSubmit, (invalidErrors) => {
          // Campos Gerenciais obrigatórios vêm aninhados em managementFieldValues.
          const { managementFieldValues, ...otherErrors } = invalidErrors;
          for (const value of [
            ...Object.values(otherErrors),
            ...Object.values(
              (managementFieldValues ?? {}) as Record<string, any>
            ),
          ]) {
            if (typeof value?.message === "string") toast.error(value.message);
          }
        })}
        onBack={onBack}
        isLast
      >
        <Box display="flex" flexDirection={"column"} gap={1}>
          <Input
            fullWidth
            label="Nome do evento *"
            placeholder="Insira o nome do evento"
            error={!!errors.name || nameExistsError}
            helperText={
              nameExistsError
                ? "Já existe um projeto com esse nome de evento"
                : errors.name
                ? "Campo Obrigatório"
                : undefined
            }
            {...register("name")}
            onChange={(e) => {
              if (e.target.value.length > 99) {
                e.target.value = e.target.value.slice(0, 99);
              }
              const value = e.target.value.trim();
              setNameExistsError(false);
              if (nameDebounceRef.current)
                clearTimeout(nameDebounceRef.current);
              if (value.length > 0) {
                nameDebounceRef.current = setTimeout(async () => {
                  try {
                    const { exists } = await ProjectService.checkEventName(
                      value,
                      project?._id
                    );
                    setNameExistsError(exists);
                  } catch {
                    // silently ignore network errors
                  }
                }, 600);
              }
            }}
          />
        </Box>

        <Box
          display={"grid"}
          gridTemplateColumns={"1fr 1fr 1fr"}
          gap={1}
          alignItems={"center"}
          width={"60%"}
        >
          <Box display="flex" flexDirection={"column"} gap={1}>
            <span
              style={{
                fontSize: 13,
                color: errors.eventScope ? "#d32f2f" : "#00000099",
                fontWeight: 400,
              }}
            >
              Evento *
            </span>
            <TriStateSwitch
              setValue={(_, value) => {
                setValue("eventScope", value, { shouldValidate: true });
              }}
              value={eventScopeWatch}
              type=""
              options={eventScopeOptions}
              align="flex-start"
            />
            {errors.eventScope && (
              <FormHelperText error sx={{ m: 0 }}>
                Campo Obrigatório
              </FormHelperText>
            )}
          </Box>
          <Box display="flex" flexDirection={"column"} gap={1}>
            <span
              style={{
                fontSize: 13,
                color: errors.hasCompetitor ? "#d32f2f" : "#00000099",
                fontWeight: 400,
              }}
            >
              Concorrência *
            </span>
            <YesOrNoSwitch
              value={hasCompetitorWatch}
              setValue={(_, value) => {
                if (value !== null)
                  setValue("hasCompetitor", value, { shouldValidate: true });
              }}
              type=""
              align="flex-start"
            />
            {errors.hasCompetitor && (
              <FormHelperText error sx={{ m: 0 }}>
                Campo Obrigatório
              </FormHelperText>
            )}
          </Box>

          <Input
            disabled={!hasCompetitorWatch}
            label="Número de concorrentes"
            {...register("numberOfCompetitors")}
            sx={{ flex: 1 }}
          />
        </Box>

        {(eventScopeWatch === EventScopeEnum.INTERNATIONAL ||
          eventScopeWatch === EventScopeEnum.BOTH) && (
          <Box border={"1px solid #eee"} p={1.5} borderRadius={3}>
            <ExchangeFields
              date={exchangeDateWatch || ""}
              currency={(exchangeCurrencyWatch ?? "BRL") as string}
              value={(exchangeValueWatch ?? 1) as number}
              onDateChange={(date) => setValue("exchangeDate", date)}
              onExchangeChange={(currency, value) =>
                updateExchangeValues("", value, currency)
              }
              disabled={false}
              dateLabel="Data cotação *"
              valueLabel="Moeda / valor dia em R$ *"
              dateError={!!errors.exchangeDate}
              dateHelperText={errors.exchangeDate?.message}
              valueHelperText={
                errors.exchangeCurrency?.message ||
                errors.exchangeValue?.message
              }
              showTitle={true}
              titleStyle={{ fontWeight: "bold", fontSize: "14px" }}
            />
          </Box>
        )}

        <Box>
          <span>Dados Gerenciais</span>
        </Box>
        <Box border={"1px solid #eee"} p={1.5} borderRadius={3}>
          <span style={{ fontSize: "14px" }}>Budget do Cliente</span>
          <Box display={"flex"} gap={4} alignItems={"baseline"}>
            <Box width={"30%"}>
              <CurrencyInputForm
                label="Valor *"
                error={!!errors.customerBudget}
                helperText={
                  errors.customerBudget ? "Campo Obrigatório" : undefined
                }
                {...register("customerBudget")}
              />
            </Box>
            <Box width={"50%"}>
              <span
                style={{
                  fontSize: "13px",
                  color: errors.customerBudgetType ? "#d32f2f" : undefined,
                }}
              >
                Tipo de estimativa*
              </span>
              <ColorToggleButton
                control={control}
                name="customerBudgetType"
                options={estimateOptions}
              />
              {errors.customerBudgetType && (
                <FormHelperText error sx={{ m: 0 }}>
                  Campo Obrigatório
                </FormHelperText>
              )}
            </Box>
            <Input
              label="Observações"
              placeholder="Insira mais detalhes"
              {...register("customerBudgetObservations")}
              fullWidth
            />
          </Box>
        </Box>
        <Box
          display={"flex"}
          gap={1}
          width={"100%"}
          p={1.5}
          border={"1px solid #eee"}
          borderRadius={3}
          alignItems={"flex-end"}
        >
          <Box width={"60%"}>
            <span style={{ fontSize: "13px" }}>Forma de faturamento</span>
            <ColorToggleButton
              control={control}
              name="billingMethod"
              options={billingOptions}
            />
            {/* <RadioGroup.Root
              label="Forma de faturamento"
              row
              value={watch("billingMethod")}
              onChange={(e) => setValue("billingMethod", e.target.value)}
            >
              {billingOptions.map(
                (
                  { value, label }: any,
                  index: React.Key | null | undefined
                ) => (
                  <RadioGroup.Item
                    key={index}
                    label={label}
                    value={value}
                    isActive={watch("billingMethod") === value}
                  />
                )
              )}
            </RadioGroup.Root> */}
          </Box>
          <Box width={"100%"}>
            <Input label="Honorários" {...register("fee")} sx={{ flex: 1 }} />
          </Box>
        </Box>
        {/* <Box border={"1px solid #eee"} p={1.5} borderRadius={3}>
          <Box display={"flex"} alignItems={"baseline"} gap={2}>
            <Select
              label="Forma de pagamento *"
              displayEmpty
              placeholder="Clique para selecionar"
              {...register("paymentMethod")}
              error={!!errors.paymentMethod}
              helperText={errors.paymentMethod?.message}
              customLabel
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <span style={{ color: "#aaa" }}>
                      Clique para selecionar
                    </span>
                  );
                }
                const option = paymentOptions.find(
                  (opt: any) => opt.value === selected
                );
                return option ? option.label : "";
              }}
            >
              {paymentOptions.map(
                (
                  { value, label }: any,
                  index: React.Key | null | undefined
                ) => (
                  <MenuItem key={index} value={value}>
                    {label}
                  </MenuItem>
                )
              )}
            </Select>
            <Input
              label="Prazo de pagamento do cliente"
              placeholder="1 dia"
              {...register("paymentTerm")}
            />
          </Box>
          <Input
            label="Observações"
            placeholder="Insira mais detalhes"
            {...register("paymentMethodObservations")}
          />
        </Box> */}
        <Box border={"1px solid #eee"} p={1.5} borderRadius={3}>
          <Box
            display="flex"
            justifyContent={"space-between"}
            gap={1}
            width={"100%"}
          >
            <Box>
              <span
                style={{
                  fontSize: "14px",
                  color: errors.acceptAdvances ? "#d32f2f" : "#222222",
                  fontWeight: 600,
                }}
              >
                Faz adiantamento ? *
              </span>
              {errors.acceptAdvances && (
                <FormHelperText error sx={{ m: 0 }}>
                  Campo Obrigatório
                </FormHelperText>
              )}
            </Box>
            <YesOrNoSwitch
              setValue={(_, value) => {
                if (value !== null)
                  setValue("acceptAdvances", value, { shouldValidate: true });
              }}
              value={acceptAdvancesWatch}
              type=""
              align="flex-start"
            />
          </Box>
          <Input
            disabled={!acceptAdvancesWatch}
            label="Observações"
            placeholder="Insira mais detalhes"
            {...register("advanceObservations")}
          />
        </Box>

        <Box border={"1px solid #eee"} p={1.5} borderRadius={3}>
          <Box
            display="flex"
            justifyContent={"space-between"}
            gap={1}
            width={"100%"}
          >
            <Box>
              <span
                style={{
                  fontSize: "14px",
                  color: errors.hasDrawnRisk ? "#d32f2f" : "#222222",
                  fontWeight: 600,
                }}
              >
                Possui risco sacado? *
              </span>
              {errors.hasDrawnRisk && (
                <FormHelperText error sx={{ m: 0 }}>
                  Campo Obrigatório
                </FormHelperText>
              )}
            </Box>
            <YesOrNoSwitch
              setValue={(_, value) => {
                if (value !== null)
                  setValue("hasDrawnRisk", value, { shouldValidate: true });
              }}
              value={hasDrawnRiskWatch}
              type=""
              align="flex-start"
            />
          </Box>

          <Box display={"grid"} gridTemplateColumns={"10% 88%"} gap={2}>
            <Input
              disabled={!hasDrawnRiskWatch}
              label="Taxa mensal"
              {...register("monthlyTax")}
              sx={{ flex: 1 }}
              placeholder="0%"
            />
            <Input
              disabled={!hasDrawnRiskWatch}
              label="Observações"
              {...register("drawnRiskObservations")}
              sx={{ flex: 1 }}
              placeholder="Insira mais detalhes"
            />
          </Box>
        </Box>
        <Box border={"1px solid #eee"} p={1.5} borderRadius={3}>
          <Box
            display="flex"
            justifyContent={"space-between"}
            gap={1}
            width={"100%"}
          >
            <Box>
              <span
                style={{
                  fontSize: "14px",
                  color: errors.directPayment ? "#d32f2f" : "#222222",
                  fontWeight: 600,
                }}
              >
                Paga fornecedores diretamente ? *
              </span>
              {errors.directPayment && (
                <FormHelperText error sx={{ m: 0 }}>
                  Campo Obrigatório
                </FormHelperText>
              )}
            </Box>
            <TriStateSwitch
              setValue={(_, value) => {
                setValue("directPayment", value, { shouldValidate: true });
              }}
              value={directPaymentWatch}
              type=""
              options={directPaymentOptions}
              align="flex-start"
            />
          </Box>
          <Input
            disabled={
              !directPaymentWatch || directPaymentWatch === DirectPaymentEnum.NO
            }
            label="Observações"
            placeholder="Insira mais detalhes"
            {...register("directPaymentsObservations")}
          />
        </Box>
        <Divider />
        <Box>
          <span>Briefing</span>
        </Box>
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          gap={2}
        >
          <DatePickerForm
            onBlur={() => {}}
            label="Data de entrada do Briefing *"
            name="briefingStartDate"
            error={errors.briefingStartDate ? "Campo Obrigatório" : undefined}
          />

          <DatePickerForm
            onBlur={() => {}}
            label="Data de envio para o Cliente *"
            name="sendDate"
            error={errors.sendDate ? "Campo Obrigatório" : undefined}
          />

          <DatePickerForm
            onBlur={() => {}}
            label="Data de apresentação para o Cliente *"
            name="presentationDate"
            error={errors.presentationDate ? "Campo Obrigatório" : undefined}
          />
        </Box>
        {isPfizerClient() && (
          <Box display={"flex"} gap={2}>
            <Input
              label="Código PFZ"
              placeholder="Insira o código"
              {...register("pfzCode")}
            />
            <Input
              label="Código MAPP"
              placeholder="Insira o código"
              {...register("mappCode")}
            />
          </Box>
        )}
        {isAbbottClient() && (
          <Box display={"flex"} gap={2}>
            <Input
              label="Código CVENT"
              placeholder="Insira o código"
              {...register("cventCode")}
            />
          </Box>
        )}
        {isEliLillyClient() && (
          <>
            <Box display={"flex"} gap={2}>
              <Input
                label="Ordem Interna"
                placeholder="Insira a ordem interna"
                {...register("eliLillyInternalOrder")}
              />
              <Input
                label="Centro de Custos"
                placeholder="Insira o centro de custos"
                {...register("eliLillyCostCenter")}
              />
            </Box>
            <Box display={"flex"} gap={2}>
              <Input
                label="Pedido de Compras"
                placeholder="Insira o pedido de compras"
                {...register("eliLillyPurchaseOrder")}
              />
              <Input
                label="VEM ID"
                placeholder="Insira o VEM ID"
                {...register("eliLillyVemId")}
              />
            </Box>
            <Box display={"flex"} gap={2}>
              <Input
                label="Produto"
                placeholder="Insira o produto"
                {...register("eliLillyProduct")}
              />
              <Input
                label="BU / Departamento"
                placeholder="Insira a BU / Departamento"
                {...register("eliLillyBuDepartment")}
              />
            </Box>
          </>
        )}
        <Input
          label="Observações"
          placeholder="Insira mais detalhes"
          {...register("observationsBriefing")}
        />
        {client?.clientSelected?.management_fields &&
          client.clientSelected.management_fields.length > 0 && (
            <>
              <Divider />
              <Box>
                <span>Campos Gerenciais</span>
              </Box>
              <Box display={"flex"} gap={2} flexWrap="wrap">
                {sortManagementFields(
                  client.clientSelected.management_fields
                ).map((field, index) => {
                  const isHighlighted = highlightedManagementFields.includes(
                    field.name
                  );
                  const label =
                    getRequiredIn(field) ===
                      ManagementFieldRequiredIn.REGISTRATION || isHighlighted
                      ? `${field.name} *`
                      : field.name;
                  const isPending =
                    isHighlighted &&
                    !String(
                      managementFieldValuesWatch?.[field.name] ?? ""
                    ).trim();
                  const fieldError =
                    ((errors.managementFieldValues as any)?.[field.name]
                      ?.message as string | undefined) ??
                    (isPending ? "Campo obrigatório" : undefined);
                  return (
                    <Box
                      key={index}
                      id={managementFieldElementId(field.name)}
                      flex={"1 1 45%"}
                      minWidth={250}
                    >
                      {field.type === ManagementFieldType.DROPDOWN ? (
                        <Controller
                          name={`managementFieldValues.${field.name}` as any}
                          control={control}
                          render={({ field: controllerField }) => {
                            const value =
                              (controllerField.value as string) || "";
                            // Valor gravado antes de a opção ser removida do
                            // cadastro continua visível até ser trocado.
                            const options =
                              value && !field.options?.includes(value)
                                ? [...(field.options ?? []), value]
                                : field.options ?? [];
                            return (
                              // Mesmo Input dos campos Texto (TextField com
                              // select) para manter rótulo, margem e altura
                              // alinhados entre os dois tipos.
                              <Input
                                select
                                label={label}
                                value={value}
                                error={!!fieldError}
                                helperText={fieldError}
                                onChange={(e) =>
                                  controllerField.onChange(e.target.value || "")
                                }
                                onBlur={controllerField.onBlur}
                                SelectProps={{
                                  displayEmpty: true,
                                  renderValue: (selected: unknown) =>
                                    (selected as string) || (
                                      // Mesmo visual do ::placeholder dos
                                      // inputs (regra global de 14px).
                                      <span
                                        style={{ fontSize: 14, opacity: 0.42 }}
                                      >
                                        {`Selecione ${field.name}`}
                                      </span>
                                    ),
                                }}
                              >
                                <MenuItem value="">
                                  <em>Não Selecionado</em>
                                </MenuItem>
                                {options.map((option) => (
                                  <MenuItem key={option} value={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </Input>
                            );
                          }}
                        />
                      ) : (
                        <Input
                          label={label}
                          placeholder={`Insira ${field.name}`}
                          error={!!fieldError}
                          helperText={fieldError}
                          {...register(
                            `managementFieldValues.${field.name}` as any
                          )}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        <Divider />

        <Box>
          <span>Endereço do evento</span>
        </Box>

        <Box display={"flex"} width={"100%"} alignItems={"flex-end"} gap={2}>
          {/* <RadioGroup.Root
            sx={{
              width: "100%",
            }}
            label="Formato de evento *"
            value={watch("eventFormat")}
            onChange={(e) => setValue("eventFormat", e.target.value)}
            row
          >
            {eventFormats.map(({ value, label }, index) => (
              <RadioGroup.Item key={index} label={label} value={value} />
            ))}
          </RadioGroup.Root> */}
          <Box
            minWidth={"30%"}
            flex={1}
            display={"flex"}
            flexDirection={"column"}
            sx={{
              // Opções personalizadas do cliente somam botões ao Formato:
              // os botões partem de 100px (como antes), o grupo cresce até a
              // largura da coluna e, sem espaço, eles encolhem até a maior
              // palavra. O texto só quebra entre palavras ("Presenc ial").
              "& .MuiToggleButtonGroup-root": {
                width: "auto",
                maxWidth: "100%",
                alignSelf: "flex-start",
              },
              "& .MuiToggleButton-root": {
                flex: "1 1 auto",
                width: 100,
                minWidth: "min-content",
                wordBreak: "normal",
                overflowWrap: "normal",
              },
            }}
          >
            <span style={{ fontSize: 13 }}>Formato do evento</span>

            <ColorToggleButton
              control={control}
              name={"eventFormat"}
              options={eventFormats}
            />
          </Box>
          <Box display={"flex"} alignItems={"flex-end"} width={"50%"}>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <Select
                  label="Tipo de evento *"
                  customLabel={true}
                  displayEmpty
                  error={!!errors.type}
                  helperText={errors.type ? "Campo Obrigatório" : undefined}
                  value={normalizeProjectType(field.value)}
                  onChange={(e) => {
                    const val = (e.target.value as string) || "";
                    field.onChange(val);
                    trigger("type");
                  }}
                  onBlur={field.onBlur}
                  renderValue={(selected: unknown) => {
                    if (!selected) {
                      return (
                        <span style={{ color: "#aaa" }}>
                          Clique para selecionar
                        </span>
                      );
                    }

                    return selected as string;
                  }}
                >
                  {eventTypes.map(({ value, label }, index) => (
                    <MenuItem key={index} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <Tooltip
              placement="left-end"
              title={
                <span style={{ fontSize: "12px" }}>
                  {projectTypeDescription[normalizeProjectType(typeWatch)] ||
                    null}
                </span>
              }
            >
              <IconButton>
                <FaQuestionCircle size={20} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box width={"10%"}>
            <Input label="Metragem" {...register("metrics")} placeholder="m²" />
          </Box>
        </Box>

        <Box border={"1px solid #eee"} p={1.5} borderRadius={3}>
          <Box
            display="flex"
            justifyContent={"space-between"}
            gap={1}
            width={"100%"}
          >
            <Box>
              <span
                style={{
                  fontSize: "14px",
                  color: errors.addressFixed ? "#d32f2f" : "#222222",
                  fontWeight: 600,
                }}
              >
                Endereço fixo? *
              </span>
              {errors.addressFixed && (
                <FormHelperText error sx={{ m: 0 }}>
                  Campo Obrigatório
                </FormHelperText>
              )}
            </Box>
            <YesOrNoSwitch
              setValue={(_, value) => {
                if (value !== null)
                  setValue("addressFixed", value, { shouldValidate: true });
              }}
              value={addressFixedWatch}
              type=""
              align="flex-start"
            />
          </Box>
          <Input
            disabled={false}
            label="Local do Evento"
            placeholder="Insira mais detalhes"
            {...register("local")}
          />
        </Box>
        {/* <Box display="flex" gap={1} alignItems={"flex-end"}>
          <Input label="Local do evento" {...register("local")} />

          <Box display="flex" flexDirection={"column"} width={140}>
            <span style={{ fontSize: 13, color: "#00000099", fontWeight: 400 }}>
              Endereço fixo *
            </span>
            <YesOrNoSwitch
              setValue={(_, value) => {
                if (value !== null) setValue("addressFixed", value);
              }}
              value={addressFixedWatch}
              type=""
              align="flex-start"
            />
          </Box>
        </Box> */}

        <Box display={"grid"} gap={4} gridTemplateColumns={"1fr 1fr"}>
          <Input
            placeholder="00000-00"
            label="CEP"
            {...register("zip_code")}
            onChange={(e) => {
              if (
                e.target.value.includes("-") ||
                e.target.value.includes(".")
              ) {
                e.target.value = e.target.value.replace(/[-.]/g, "");
              }
              if (e.target.value.length > 8) {
                e.target.value = e.target.value.slice(0, 8);
              }
            }}
            onKeyDown={(e) => {
              // if (!/[0-9]/.test(e.key)) {
              //   e.preventDefault();
              // }
            }}
            onBlur={(e) => {
              fetchCepData(e.target.value);
              setValue("zip_code", e.target.value);
            }}
          />
          <Input label="País" placeholder="País" {...register("country")} />
        </Box>

        <Box display={"grid"} gap={4} gridTemplateColumns={"1fr 1fr"}>
          <Input placeholder="Estado" label="Estado" {...register("state")} />

          <Input placeholder="Cidade" label="Cidade" {...register("city")} />
        </Box>

        <Box display={"grid"} gap={4} gridTemplateColumns={"1fr 1fr"}>
          <Input
            placeholder="Bairro"
            label="Bairro"
            {...register("neighborhood")}
          />
          <Input
            placeholder="Rua, Avenida"
            label="Logradouro"
            {...register("complement")}
          />
        </Box>

        <Box display={"grid"} gap={4} gridTemplateColumns={"1fr 1fr"}>
          <Input
            placeholder="Número"
            label="Número"
            {...register("number")}
            onChange={(e) => {
              const numberRegex = new RegExp("^[0-9]*$");
              if (!numberRegex.test(e.target.value)) {
                e.target.value = e.target.value.replace(/\D/g, "");
              }
            }}
          />
          <Input
            placeholder="Ap, quadra"
            label="Complemento"
            {...register("complement2")}
          />
        </Box>

        <Box>
          <Box py={1}>
            {/* <RadioGroup.Root
              label="Forma de faturamento"
              value={watch("billingMethod")}
              onChange={(e) => setValue("billingMethod", e.target.value)}
            >
              {billingOptions.map(
                (
                  { value, label }: any,
                  index: React.Key | null | undefined
                ) => (
                  <RadioGroup.Item key={index} label={label} value={value} />
                )
              )}
            </RadioGroup.Root> */}
          </Box>
          {/* <Box py={1}>
            <RadioGroup.Root
              label="Forma de pagamento"
              value={watch("paymentMethod")}
              onChange={(e) => setValue("paymentMethod", e.target.value)}
            >
              
            </RadioGroup.Root>
          </Box> */}
        </Box>
        <Divider />
        <span>Público</span>
        <Box display={"grid"} gap={4} gridTemplateColumns={"1fr 1fr 1fr"}>
          <Input
            placeholder="Insira o perfil do evento"
            label="Perfil do evento"
            {...register("eventProfile")}
          />
          <Input
            placeholder="Insira o público do evento"
            label="Público"
            {...register("audience")}
          />
          <Input
            placeholder="Insira o número de pessoas"
            label="Nº de pessoas *"
            error={!!errors.numberOfPeople}
            helperText={errors.numberOfPeople?.message}
            {...register("numberOfPeople", {
              onChange: (e) => {
                const value = e.target.value;
                // Allow only positive integers - remove any non-digit characters
                const cleanedValue = value.replace(/[^0-9]/g, "");
                e.target.value = cleanedValue;
                setValue(
                  "numberOfPeople",
                  cleanedValue ? parseInt(cleanedValue) : ("" as any)
                );
              },
            })}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              onKeyDown: (e: any) => {
                // Prevent minus, plus, dot, comma, 'e' and other non-numeric keys
                if (["-", "+", ".", ",", "e", "E", " "].includes(e.key)) {
                  e.preventDefault();
                }
              },
            }}
          />
        </Box>
        <Divider />
        <span>Programação</span>

        <Box
          display={"grid"}
          gap={4}
          gridTemplateColumns={"1fr 1fr 1fr"}
          alignItems={"baseline"}
        >
          <DatePickerForm
            label="Data  início *"
            name="startDate"
            onBlur={handleBlur}
            control={control}
            minDate={!isUserManager ? new Date() : undefined}
            error={errors.startDate?.message}
          />

          <Controller
            name="endDate"
            control={control}
            render={({ field: { onChange, value, ...restField } }) => (
              <Box width={"100%"}>
                <InputLabel sx={{ fontSize: 13 }} error={!!errors.endDate}>
                  Data fim *
                </InputLabel>
                <DatePicker
                  label=""
                  onChange={(event) => onChange(event)}
                  {...restField}
                  value={value ?? null}
                  minDate={startDateWatch || undefined}
                  defaultCalendarMonth={startDateWatch || undefined}
                  slotProps={{
                    textField: {
                      error: !!errors.endDate,
                      helperText: errors.endDate
                        ? "Campo Obrigatório"
                        : undefined,
                      FormHelperTextProps: { sx: { marginLeft: 0 } },
                      onBlur: handleBlur,
                    },
                  }}
                />
              </Box>
            )}
          />
          <Box>
            <span
              style={{
                fontSize: 13,
                color: errors.sequenceDays ? "#d32f2f" : "#00000099",
                fontWeight: 400,
              }}
            >
              Dias corridos *
            </span>
            <br />
            <YesOrNoSwitch
              setValue={(_, value) => {
                if (value !== null)
                  setValue("sequenceDays", value, { shouldValidate: true });
              }}
              value={sequenceDaysWatch}
              type=""
              align="flex-start"
            />
            {errors.sequenceDays && (
              <FormHelperText error sx={{ m: 0 }}>
                Campo Obrigatório
              </FormHelperText>
            )}
          </Box>
        </Box>

        <Box
          display={"grid"}
          gap={4}
          gridTemplateColumns={"1fr 1fr 1fr"}
          alignItems={"baseline"}
        >
          <Input label="Hora início" {...register("hourInit")} type="time" />
          <Input label="Hora fim" {...register("hourEnd")} type="time" />
          <Box>
            <span
              style={{
                fontSize: 13,
                color: errors.sameTimeEveryDay ? "#d32f2f" : "#00000099",
                fontWeight: 400,
              }}
            >
              Repete todos os dias? *
            </span>
            <br />
            <YesOrNoSwitch
              setValue={(_, value) => {
                if (value !== null)
                  setValue("sameTimeEveryDay", value, { shouldValidate: true });
              }}
              value={sameTimeEveryDayWatch}
              type=""
              align="flex-start"
            />
            {errors.sameTimeEveryDay && (
              <FormHelperText error sx={{ m: 0 }}>
                Campo Obrigatório
              </FormHelperText>
            )}
          </Box>
        </Box>
        <Box
          display={"grid"}
          gap={4}
          gridTemplateColumns={"1fr 1fr"}
          alignItems={"baseline"}
        >
          <Input
            label="Descreva horários específicos"
            {...register("schedulleObservations")}
            placeholder="Ex: Sábado encerramos às 14:00"
          />
          <Input
            label="Duração do evento"
            {...register("duration")}
            type="number"
            disabled
            placeholder="0 dias"
          />

          {/* <Box display="flex" flexDirection={"column"} gap={1} width={250}>
            <span style={{ fontSize: 13, color: "#00000099", fontWeight: 400 }}>
              Mesmo horário início e fim todos os dias *
            </span>
            <YesOrNoSwitch
              setValue={(_, value) => {
                if (value !== null) setValue("sameTimeEveryDay", value);
              }}
              value={sameTimeEveryDayWatch}
              type=""
              align="flex-start"
            />
           </Box> */}
        </Box>
        <Box border={"1px solid #eee"} p={1.5} borderRadius={3}>
          <EventPeriodsInput
            periods={hostingPeriods}
            onChange={setHostingPeriods}
          />
        </Box>
        <Divider />
        <span>Detalhes extras</span>
        <Input
          label="Objetivo do evento"
          placeholder="Insira mais detalhes"
          {...register("objective")}
        />
        <Input
          label="Desafio do cliente"
          placeholder="Insira mais detalhes"
          {...register("customerChallenge")}
        />
        <Input
          label="Observações"
          placeholder="Insira mais detalhes"
          {...register("observationsEvent")}
        />
      </TabContent>
    </FormProvider>
  );
};

export default ProjectEvent;
